/**
 * Declarative level format.
 *
 * Both the physics world and the visual scene are built from the same
 * `LevelData`, which keeps collision and visuals consistent by construction.
 * Solids reference surface materials by stable id (see SurfaceMaterials.ts).
 */
import type { QuatLike } from '../core/Vec3';
import type { MaterialRegistry, SurfaceMaterialDef } from './SurfaceMaterials';
import { MAX_COLLIDER_HALF_EXTENT } from './LevelBuilders';

export type Vec3Tuple = readonly [number, number, number];

export type SolidShape =
  | { type: 'box'; halfExtents: Vec3Tuple }
  | { type: 'cylinder'; radius: number; halfHeight: number }
  /**
   * Right triangular prism inside the box ±halfExtents: full bottom face, a
   * vertical back face at local −Z, and a slope from the top of the back face
   * down to the bottom front edge at +Z (it rises toward −Z). A convex hull
   * collider (Phase 4).
   */
  | { type: 'wedge'; halfExtents: Vec3Tuple };

/** The six local-space vertices of a wedge (see SolidShape). */
export function wedgePoints(h: Vec3Tuple): [number, number, number][] {
  const [x, y, z] = h;
  return [
    [-x, -y, -z], [x, -y, -z], [-x, -y, z], [x, -y, z],
    [-x, y, -z], [x, y, -z],
  ];
}

export interface SolidDef {
  id: string;
  shape: SolidShape;
  position: Vec3Tuple;
  /** Euler rotation in degrees, applied in X, then Y, then Z order (intrinsic XYZ). */
  rotationDeg?: Vec3Tuple;
  material: string;
  /** Optional tag used by the debug UI and tests to find a solid. */
  tag?: string;
}

export type TriggerKind = 'start' | 'finish' | 'checkpoint';

export interface SpawnPoint {
  /** Position of the capsule's bottom point. */
  position: Vec3Tuple;
  yawDeg: number;
}

export interface TriggerDef {
  id: string;
  kind: TriggerKind;
  /** Axis-aligned box: centre and half extents. */
  center: Vec3Tuple;
  halfExtents: Vec3Tuple;
  material: string;
  /** Checkpoints: ordering index (0-based) and respawn point. */
  checkpointIndex?: number;
  spawn?: SpawnPoint;
}

export interface LevelData {
  id: string;
  name: string;
  /** Bump whenever geometry changes so recorded times/replays can be invalidated. */
  version: number;
  /**
   * Gameplay content fingerprint (SHA-256 hex) of a compiled track (Phase 4).
   * When present, records are keyed by it instead of `version`.
   */
  contentHash?: string;
  materials?: readonly SurfaceMaterialDef[];
  solids: readonly SolidDef[];
  triggers: readonly TriggerDef[];
  start: SpawnPoint;
  /** Y below which the player is considered out of bounds (dies). */
  killPlaneY: number;
  /** Appearance preset (Phase 9); absent = 'violet-dusk'. Never read by the simulation. */
  atmosphere?: 'violet-dusk' | 'dungeon';
  /** Purely decorative lights (lanterns). Never read by the simulation. */
  lights?: readonly LightDef[];
  /** Purely decorative labels drawn near geometry (e.g. chamber names). */
  labels?: readonly { text: string; position: Vec3Tuple }[];
  /** Named developer teleport targets (research chambers). Validated like spawns. */
  waypoints?: readonly { name: string; spawn: SpawnPoint }[];
}

export interface LightDef {
  position: Vec3Tuple;
  color: number;
  intensity: number;
  distance: number;
}

const DEG = Math.PI / 180;

/** Intrinsic X→Y→Z Euler (degrees) to quaternion. Matches THREE.Euler order 'XYZ'. */
export function eulerDegToQuat(r: Vec3Tuple | undefined, out: QuatLike): QuatLike {
  const [rx, ry, rz] = r ?? [0, 0, 0];
  const c1 = Math.cos((rx * DEG) / 2);
  const c2 = Math.cos((ry * DEG) / 2);
  const c3 = Math.cos((rz * DEG) / 2);
  const s1 = Math.sin((rx * DEG) / 2);
  const s2 = Math.sin((ry * DEG) / 2);
  const s3 = Math.sin((rz * DEG) / 2);
  out.x = s1 * c2 * c3 + c1 * s2 * s3;
  out.y = c1 * s2 * c3 - s1 * c2 * s3;
  out.z = c1 * c2 * s3 + s1 * s2 * c3;
  out.w = c1 * c2 * c3 - s1 * s2 * s3;
  return out;
}

const finite3 = (t: unknown): t is Vec3Tuple =>
  Array.isArray(t) && t.length === 3 && t.every((n) => typeof n === 'number' && Number.isFinite(n));

/**
 * Validates level data against a material registry. Returns a list of human
 * readable errors (empty when valid). Geometric validation that needs the
 * physics world (e.g. spawn clearance) lives in LevelLoader.
 */
export function validateLevelData(level: LevelData, materials: MaterialRegistry): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  const checkId = (id: string, what: string): void => {
    if (!id) errors.push(`${what} has an empty id`);
    if (ids.has(id)) errors.push(`duplicate id "${id}"`);
    ids.add(id);
  };
  const checkMaterial = (matId: string, owner: string, wantTrigger: boolean): void => {
    if (!materials.has(matId)) {
      errors.push(`${owner}: unknown material "${matId}"`);
      return;
    }
    const isTrigger = materials.get(matId).kind === 'trigger';
    if (wantTrigger && !isTrigger) errors.push(`${owner}: triggers must use a trigger material`);
    if (!wantTrigger && isTrigger) errors.push(`${owner}: solids cannot use trigger material "${matId}"`);
  };

  for (const s of level.solids) {
    checkId(s.id, 'solid');
    checkMaterial(s.material, `solid ${s.id}`, false);
    if (!finite3(s.position)) errors.push(`solid ${s.id}: invalid position`);
    if (s.rotationDeg && !finite3(s.rotationDeg)) errors.push(`solid ${s.id}: invalid rotation`);
    const boxLike = s.shape.type === 'box' || s.shape.type === 'wedge';
    const extents = s.shape.type === 'cylinder' ? [s.shape.radius, s.shape.halfHeight] : s.shape.halfExtents;
    if (boxLike && !finite3(extents)) {
      errors.push(`solid ${s.id}: invalid ${s.shape.type} half extents`);
    } else if (extents.some((h) => !(h > 0))) {
      errors.push(`solid ${s.id}: shape dimensions must be positive`);
    } else if (extents.some((h) => h > MAX_COLLIDER_HALF_EXTENT)) {
      errors.push(`solid ${s.id}: half extent exceeds ${MAX_COLLIDER_HALF_EXTENT} m; tile it (see LevelBuilders.tiledBox)`);
    }
  }

  let starts = 0;
  let finishes = 0;
  const cpIndices = new Set<number>();
  for (const t of level.triggers) {
    checkId(t.id, 'trigger');
    checkMaterial(t.material, `trigger ${t.id}`, true);
    if (!finite3(t.center) || !finite3(t.halfExtents) || t.halfExtents.some((h) => h <= 0)) {
      errors.push(`trigger ${t.id}: invalid box`);
    }
    if (t.kind === 'start') starts++;
    if (t.kind === 'finish') finishes++;
    if (t.kind === 'checkpoint') {
      if (t.checkpointIndex === undefined || !Number.isInteger(t.checkpointIndex) || t.checkpointIndex < 0) {
        errors.push(`checkpoint ${t.id}: checkpointIndex must be a non-negative integer`);
      } else if (cpIndices.has(t.checkpointIndex)) {
        errors.push(`checkpoint ${t.id}: duplicate checkpointIndex ${t.checkpointIndex}`);
      } else {
        cpIndices.add(t.checkpointIndex);
      }
      if (!t.spawn || !finite3(t.spawn.position)) errors.push(`checkpoint ${t.id}: requires a spawn point`);
    }
  }
  if (starts !== 1) errors.push(`level must have exactly one start trigger (found ${starts})`);
  if (finishes < 1) errors.push('level must have at least one finish trigger');
  if (!finite3(level.start.position)) errors.push('invalid start position');
  if (!Number.isFinite(level.killPlaneY)) errors.push('invalid killPlaneY');
  if (level.start.position[1] <= level.killPlaneY) errors.push('start position is below the kill plane');
  for (const w of level.waypoints ?? []) {
    if (!w.name) errors.push('waypoint has an empty name');
    if (!finite3(w.spawn.position) || !Number.isFinite(w.spawn.yawDeg)) errors.push(`waypoint "${w.name}": invalid spawn`);
  }
  return errors;
}
